import React, { useCallback, useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'
import ConfirmModal from './ConfirmModal'
import './CadastroTiposMovimentacao.css'
import './CadastroInversoresModulos.css'

const emptyForm = { marca: '', modelo: '', potencia: '' }

function CatalogoPainel({ tipo, unidadePotencia, tabela }) {
  const [itens, setItens] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState({ text: '', type: '' })
  const [form, setForm] = useState(emptyForm)
  const [editingId, setEditingId] = useState(null)
  const [editForm, setEditForm] = useState(emptyForm)
  const [confirmDelete, setConfirmDelete] = useState({ show: false, item: null })

  const loadItens = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from(tabela)
        .select('id, marca, modelo, potencia')
        .order('marca')
        .order('modelo')
      if (error) throw error
      setItens(data || [])
    } catch (err) {
      setMsg({ text: err.message || `Erro ao carregar ${tipo}.`, type: 'error' })
    } finally {
      setLoading(false)
    }
  }, [tabela, tipo])

  useEffect(() => {
    loadItens()
  }, [loadItens])

  const handleCadastrar = async (e) => {
    e.preventDefault()
    const marca = form.marca.trim()
    const modelo = form.modelo.trim()
    if (!marca || !modelo) {
      setMsg({ text: 'Informe marca e modelo.', type: 'error' })
      return
    }
    setSaving(true)
    setMsg({ text: '', type: '' })
    try {
      const potencia = form.potencia === '' ? null : parseFloat(String(form.potencia).replace(',', '.'))
      const { error } = await supabase
        .from(tabela)
        .insert([{ marca, modelo, potencia: Number.isNaN(potencia) ? null : potencia }])
      if (error) throw error
      setMsg({ text: `${tipo} cadastrado com sucesso.`, type: 'success' })
      setForm(emptyForm)
      loadItens()
      setTimeout(() => setMsg({ text: '', type: '' }), 3000)
    } catch (err) {
      if (err.code === '23505') {
        setMsg({ text: 'Já existe um cadastro com esta marca e modelo.', type: 'error' })
      } else {
        setMsg({ text: err.message || 'Erro ao cadastrar.', type: 'error' })
      }
    } finally {
      setSaving(false)
    }
  }

  const handleSalvarEdicao = async () => {
    const marca = editForm.marca.trim()
    const modelo = editForm.modelo.trim()
    if (!marca || !modelo) return
    setSaving(true)
    try {
      const potencia = editForm.potencia === '' ? null : parseFloat(String(editForm.potencia).replace(',', '.'))
      const { error } = await supabase
        .from(tabela)
        .update({ marca, modelo, potencia: Number.isNaN(potencia) ? null : potencia })
        .eq('id', editingId)
      if (error) throw error
      setMsg({ text: `${tipo} atualizado.`, type: 'success' })
      setEditingId(null)
      loadItens()
      setTimeout(() => setMsg({ text: '', type: '' }), 3000)
    } catch (err) {
      if (err.code === '23505') {
        setMsg({ text: 'Já existe um cadastro com esta marca e modelo.', type: 'error' })
      } else {
        setMsg({ text: err.message || 'Erro ao atualizar.', type: 'error' })
      }
    } finally {
      setSaving(false)
    }
  }

  const excluirItem = async () => {
    const item = confirmDelete.item
    setConfirmDelete({ show: false, item: null })
    if (!item) return
    try {
      const { error } = await supabase.from(tabela).delete().eq('id', item.id)
      if (error) throw error
      loadItens()
    } catch (err) {
      setMsg({ text: err.message || 'Erro ao excluir.', type: 'error' })
    }
  }

  return (
    <>
      <section className="cadastro-tipos-card">
        <h2 className="cadastro-tipos-card-titulo">
          <span className="cadastro-tipos-card-icone">➕</span>
          Novo {tipo.toLowerCase()}
        </h2>
        <form onSubmit={handleCadastrar} className="cadastro-tipos-form">
          <label className="cadastro-tipos-label">
            Marca
            <input
              type="text"
              className="cadastro-tipos-input"
              placeholder="Ex: Growatt"
              value={form.marca}
              onChange={(e) => setForm((prev) => ({ ...prev, marca: e.target.value }))}
              maxLength={150}
            />
          </label>
          <label className="cadastro-tipos-label">
            Modelo
            <input
              type="text"
              className="cadastro-tipos-input"
              placeholder="Ex: MIN 5000TL-X"
              value={form.modelo}
              onChange={(e) => setForm((prev) => ({ ...prev, modelo: e.target.value }))}
              maxLength={150}
            />
          </label>
          <label className="cadastro-tipos-label">
            Potência ({unidadePotencia})
            <input
              type="number"
              className="cadastro-tipos-input"
              placeholder={unidadePotencia === 'kW' ? 'Ex: 5.0' : 'Ex: 550'}
              step="0.01"
              min="0"
              value={form.potencia}
              onChange={(e) => setForm((prev) => ({ ...prev, potencia: e.target.value }))}
            />
          </label>
          <button type="submit" className="cadastro-tipos-btn-cadastrar" disabled={saving}>
            {saving ? 'Cadastrando...' : 'Cadastrar'}
          </button>
        </form>
      </section>

      {msg.text && (
        <div className={`cadastro-tipos-msg cadastro-tipos-msg-${msg.type}`} role="alert">
          {msg.text}
        </div>
      )}

      <section className="cadastro-tipos-lista">
        <h2 className="cadastro-tipos-lista-titulo">
          {tipo === 'Inversor' ? 'Inversores cadastrados' : 'Módulos cadastrados'}
        </h2>
        {loading ? (
          <p className="cadastro-tipos-loading">Carregando...</p>
        ) : (
          <div className="cadastro-tipos-tabela-wrap">
            <table className="cadastro-tipos-tabela">
              <thead>
                <tr>
                  <th>Marca</th>
                  <th>Modelo</th>
                  <th>Potência</th>
                  <th className="cadastro-tipos-th-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {itens.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="cadastro-tipos-empty">
                      Nenhum {tipo.toLowerCase()} cadastrado.
                    </td>
                  </tr>
                ) : (
                  itens.map((item) => (
                    <tr key={item.id}>
                      <td>
                        {editingId === item.id ? (
                          <input
                            type="text"
                            className="cadastro-tipos-input cadastro-tipos-input-inline"
                            value={editForm.marca}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, marca: e.target.value }))}
                          />
                        ) : item.marca}
                      </td>
                      <td>
                        {editingId === item.id ? (
                          <input
                            type="text"
                            className="cadastro-tipos-input cadastro-tipos-input-inline"
                            value={editForm.modelo}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, modelo: e.target.value }))}
                          />
                        ) : item.modelo}
                      </td>
                      <td>
                        {editingId === item.id ? (
                          <input
                            type="number"
                            className="cadastro-tipos-input cadastro-tipos-input-inline"
                            step="0.01"
                            min="0"
                            value={editForm.potencia}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, potencia: e.target.value }))}
                          />
                        ) : (
                          item.potencia != null ? `${item.potencia} ${unidadePotencia}` : '–'
                        )}
                      </td>
                      <td className="cadastro-tipos-td-acoes">
                        {editingId === item.id ? (
                          <>
                            <button type="button" className="cadastro-tipos-btn-salvar-inline" onClick={handleSalvarEdicao} disabled={saving}>
                              Salvar
                            </button>
                            <button type="button" className="cadastro-tipos-btn-cancelar-inline" onClick={() => setEditingId(null)}>
                              Cancelar
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="cadastro-tipos-btn-acao"
                              onClick={() => {
                                setEditingId(item.id)
                                setEditForm({
                                  marca: item.marca || '',
                                  modelo: item.modelo || '',
                                  potencia: item.potencia ?? ''
                                })
                              }}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              className="cadastro-tipos-btn-acao cadastro-tipos-btn-excluir"
                              onClick={() => setConfirmDelete({ show: true, item })}
                            >
                              Excluir
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <ConfirmModal
        show={confirmDelete.show}
        title={`Excluir ${tipo.toLowerCase()}`}
        message={`Excluir ${confirmDelete.item?.marca || ''} ${confirmDelete.item?.modelo || ''}?`}
        type="danger"
        onConfirm={excluirItem}
        onCancel={() => setConfirmDelete({ show: false, item: null })}
      />
    </>
  )
}

const CadastroInversoresModulos = ({ onBack }) => {
  const [aba, setAba] = useState('inversores')

  return (
    <div className="cadastro-tipos">
      <div className="cadastro-tipos-header">
        <button type="button" className="cadastro-tipos-btn-back" onClick={onBack} aria-label="Voltar">
          ← Voltar
        </button>
        <h1 className="cadastro-tipos-title">Inversores e Módulos</h1>
      </div>
      <p className="cadastro-tipos-descricao">
        Cadastre os modelos usados no Kit Solar do cliente. Na hora do preenchimento, o usuário pesquisa e seleciona o modelo.
      </p>

      <div className="cadastro-equip-tabs">
        <button
          type="button"
          className={`cadastro-equip-tab${aba === 'inversores' ? ' is-active' : ''}`}
          onClick={() => setAba('inversores')}
        >
          Inversores
        </button>
        <button
          type="button"
          className={`cadastro-equip-tab${aba === 'modulos' ? ' is-active' : ''}`}
          onClick={() => setAba('modulos')}
        >
          Módulos
        </button>
      </div>

      {aba === 'inversores' ? (
        <CatalogoPainel tipo="Inversor" unidadePotencia="kW" tabela="inversores" />
      ) : (
        <CatalogoPainel tipo="Módulo" unidadePotencia="W" tabela="modulos" />
      )}
    </div>
  )
}

export default CadastroInversoresModulos
